import React from 'react';
import styled from 'styled-components';
import {useIntl, FormattedMessage} from 'react-intl';
import {Icon, SubHeading, Text} from '@forecast-it/design-system';

// Shared shell for ChatPanel's empty-transcript intros: EmptyState (the generic Nova greeting)
// wraps this with its own bodyId/suggestionIds, and ChatPanel renders it directly for the
// report-builder variant. The two differ only in body copy, suggestion text, and what a
// suggestion click does (populate the input vs. send immediately) — all passed in as props, so
// there is exactly one place this chrome is defined.
const Wrapper = styled.div`
	flex: 1 1 auto;
	display: flex;
	flex-direction: column;
	align-items: stretch;
	padding: 8px 4px 0;
`;

const IconWrapper = styled.div`
	margin-bottom: 16px;
	color: #6b6d7b;
`;

const Suggestions = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin-top: 20px;
`;

const SuggestionCard = styled.button`
	text-align: left;
	background: #ffffff;
	border: 1px solid #d3d3df;
	border-radius: 8px;
	padding: 12px 14px;
	cursor: pointer;
	transition: border-color 100ms ease, box-shadow 100ms ease;

	&:hover {
		border-color: #0004db;
		box-shadow: 0 1px 2px 0 rgba(35, 35, 52, 0.06), 0 1px 1px 0 rgba(35, 35, 52, 0.04);
	}
`;

type ChatIntroProps = {
	bodyId: string;
	suggestionIds: ReadonlyArray<string>;
	onSuggestionClick: (text: string) => void;
};

export function ChatIntro({bodyId, suggestionIds, onSuggestionClick}: ChatIntroProps): JSX.Element {
	const {formatMessage} = useIntl();

	return (
		<Wrapper>
			<IconWrapper>
				<Icon icon="sparkle" size="l" />
			</IconWrapper>
			<Text size="2" color="auxiliary">
				<FormattedMessage id={bodyId} />
			</Text>
			<Suggestions>
				{suggestionIds.map(id => {
					const title = formatMessage({id});
					return (
						<SuggestionCard type="button" key={id} onClick={() => onSuggestionClick(title)}>
							<SubHeading size="2">{title}</SubHeading>
						</SuggestionCard>
					);
				})}
			</Suggestions>
		</Wrapper>
	);
}
